// src/pages/VehicleRegister.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { Car, X, AlertCircle, CheckCircle } from "lucide-react";
import { fetchVehicles } from "../actions/VehicleActions";

const VehicleRegister = ({ isOpen, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);

  const authKey =
    auth?.authKey ||
    auth?.userDetails?.authKey ||
    "HQZQpMO3WSjnurGdn4/GZLxCeuEnPB0qwoiVggtWuZc";

  const [vehicleData, setVehicleData] = useState({
    vehicleId: "",
    vehicleType: "",
    weightCat: "",
  });

  const [existingVehicles, setExistingVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Vehicle type options with labels
  const vehicleTypes = [
    // { value: "C", label: "Car" },
    { value: "F", label: "Forklift" },
    // { value: "V", label: "Van" },
    // { value: "L", label: "Lorry" },
    // { value: "S", label: "SUV" },
    // { value: "CA", label: "Cab" },
    // { value: "B", label: "Bus" },
    { value: "CH", label: "Cherry Picker" },
    { value: "T", label: "Trailer" },
    { value: "CR", label: "Crane" },
  ];

  // Fetch vehicle IDs
  useEffect(() => {
    if (!isOpen) return;

    const fetchData = async () => {
      try {
        const vehicleList = await dispatch(fetchVehicles());
        const vehicleIds = vehicleList.map((v) => v.vehicleId?.toUpperCase());
        setExistingVehicles(vehicleIds);
      } catch (err) {
        console.error("Error loading vehicles:", err);
        setExistingVehicles([]);
      }
    };

    fetchData();
  }, [authKey, dispatch, isOpen]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setVehicleData({
        vehicleId: "",
        vehicleType: "",
        weightCat: "",
      });
      setError("");
      setSuccess("");
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value.toUpperCase();

    if (name === "vehicleId") {
      formattedValue = formattedValue.replace(/[^A-Z0-9-]/g, "");
    } else if (name === "weightCat") {
      formattedValue = formattedValue.replace(/[^A-Z0-9]/g, "");
    }

    setVehicleData((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));

    setError("");
    setSuccess("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    const enteredId = vehicleData.vehicleId.trim().toUpperCase();
    const vehicleIdRegex = /^([A-Z]{2,3}|[0-9]{2,3})-[0-9]{4}$/;

    // Validations
    if (!enteredId) {
      setError("Vehicle ID is required");
      setLoading(false);
      return;
    }

    if (!vehicleIdRegex.test(enteredId)) {
      setError("Invalid format. Use ABC-1234 or 12-3456");
      setLoading(false);
      return;
    }

    if (existingVehicles.includes(enteredId)) {
      setError("This Vehicle ID already exists");
      setLoading(false);
      return;
    }

    if (!vehicleData.vehicleType) {
      setError("Vehicle Type is required");
      setLoading(false);
      return;
    }

    if (!vehicleData.weightCat) {
      setError("Weight category is required");
      setLoading(false);
      return;
    }

    if (vehicleData.weightCat.length > 3) {
      setError("Weight category must be max 3 characters");
      setLoading(false);
      return;
    }

    const payload = [
      {
        vehicleId: enteredId,
        vehicleType: vehicleData.vehicleType,
        weightCat: vehicleData.weightCat.substring(0, 3),
        driverSno: "",
        status: "A",
        bStatus: "",
        latitude: "0",
        longitude: "0",
        oStatus: "F",
      },
    ];

    try {
      const response = await axios.post(
        "https://aeroviewbackend.dockyardsoftware.com/VehicleRegister/PostVehicleDetails",
        payload,
        { headers: { "Content-Type": "application/json", "auth-key": authKey } }
      );

      if (response.data.statusCode === 200) {
        setSuccess("Vehicle registered successfully!");
        setVehicleData({
          vehicleId: "",
          vehicleType: "",
          weightCat: "",
        });
        if (onSuccess) onSuccess();
        setTimeout(() => {
          setSuccess("");
          onClose();
        }, 1500);
      } else {
        setError("Registration failed: " + response.data.result);
      }
    } catch (err) {
      console.error("Axios Error:", err);
      setError("Failed to register vehicle. Check network/API.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-gray-100 rounded-lg">
              <Car size={16} className="text-gray-600" />
            </div>
            <h2 className="text-sm font-semibold text-gray-900">
              Add New Vehicle
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition"
          >
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Error/Success Messages */}
          {error && (
            <div className="mb-3 p-2 bg-red-50 border border-red-100 rounded-lg flex items-center gap-2">
              <AlertCircle size={14} className="text-red-500 flex-shrink-0" />
              <p className="text-xs text-red-600">{error}</p>
            </div>
          )}
          {success && (
            <div className="mb-3 p-2 bg-emerald-50 border border-emerald-100 rounded-lg flex items-center gap-2">
              <CheckCircle size={14} className="text-emerald-500 flex-shrink-0" />
              <p className="text-xs text-emerald-600">{success}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Vehicle ID */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Vehicle ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="vehicleId"
                value={vehicleData.vehicleId}
                onChange={handleChange}
                placeholder="e.g., ABC-1234"
                required
                maxLength={8}
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition"
              />
              <p className="text-xs text-gray-400 mt-1">
                Format: ABC-1234 or 12-3456
              </p>
            </div>

            {/* Vehicle Type */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Vehicle Type <span className="text-red-500">*</span>
              </label>
              <select
                name="vehicleType"
                value={vehicleData.vehicleType}
                onChange={handleChange}
                required
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition bg-white"
              >
                <option value="">Select Type</option>
                {vehicleTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Weight Category */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Weight Category <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="weightCat"
                value={vehicleData.weightCat}
                onChange={handleChange}
                placeholder="Max 3 characters"
                required
                maxLength={3}
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition"
              />
              <p className="text-xs text-gray-400 mt-1">
                Example: 1T, 2T, 5T, 10T
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-3 py-1.5 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Registering..." : "Register"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VehicleRegister;